/**
 * Job completion feedback registry.
 *
 * Generic, extensible mapping from a terminal job to the toast it should raise.
 * Features register a resolver for their `job_type` (e.g. plan generation →
 * "View / Activate"); everything else falls back to the default, which already
 * offers a Download for any job that produced a `file_url` (covers the exports).
 */

import {
  JobProgressUtils,
  type JobProgress,
} from '@personal-trainer-app/shared';

export interface JobCompletionAction {
  label: string;
  /** External/explicit URL to open. Used when no `onClick` is supplied. */
  href?: string;
  /** Custom handler (e.g. in-app navigation). Takes precedence over `href`. */
  onClick?: () => void;
}

export interface JobCompletionFeedback {
  variant: 'success' | 'error';
  title?: string;
  message: string;
  action?: JobCompletionAction;
}

export type JobCompletionResolver = (
  progress: JobProgress
) => JobCompletionFeedback | null;

const registry = new Map<string, JobCompletionResolver>();

/**
 * Resolve the feedback for a terminal job, using its resolver or the default.
 * `null` is a job that ends without a toast: one held for review is neither
 * done nor broken, and its own notification ("Plan in review") says so once.
 */
export function resolveJobCompletion(
  progress: JobProgress
): JobCompletionFeedback | null {
  if (JobProgressUtils.isHeldJob(progress)) return null;

  const custom = progress.job_type
    ? registry.get(progress.job_type)
    : undefined;
  return custom ? custom(progress) : defaultJobCompletion(progress);
}

/** The fallback used for any job without a registered resolver. */
export function defaultJobCompletion(
  progress: JobProgress
): JobCompletionFeedback {
  const label = JobProgressUtils.getJobLabel(progress);

  if (progress.status === 'failed') {
    return {
      variant: 'error',
      title: `${label} failed`,
      message: progress.message ?? 'Something went wrong. Please try again.',
    };
  }

  const fileUrl =
    typeof progress.metadata.file_url === 'string'
      ? progress.metadata.file_url
      : undefined;

  return {
    variant: 'success',
    title: `${label} ready`,
    message: progress.message ?? 'Done.',
    ...(fileUrl ? { action: { label: 'Download', href: fileUrl } } : {}),
  };
}
